import { useEffect, useRef, useState } from "react";
import { Satellite, Play, Square } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { BrandMark, useBrand } from "@/context/BrandContext";

const PING_MS = 15000;

export default function Driver() {
  const brand = useBrand();
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [active, setActive] = useState(false);
  const [pings, setPings] = useState(0);
  const [fix, setFix] = useState(null);
  const [status, setStatus] = useState("IDLE");
  const watchRef = useRef(null);
  const timerRef = useRef(null);
  const lastRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/public/vehicles`)
      .then(({ data }) => {
        setVehicles(data);
        setVehicleId((prev) => prev || data[0]?.id || "");
      })
      .catch(() => setStatus("COULD NOT LOAD VEHICLES"));
  }, []);

  const send = async (coords) => {
    if (!vehicleId) return;
    try {
      await axios.post(`${API}/ingest/location`, {
        vehicle_id: vehicleId,
        lat: coords.latitude,
        lng: coords.longitude,
        speed_kmh: coords.speed ? Math.max(0, coords.speed * 3.6) : 0,
        ignition_state: "on",
        heading: coords.heading ?? null,
      });
      setPings((p) => p + 1);
      setStatus("PING SENT");
    } catch (e) {
      setStatus("PING FAILED");
    }
  };

  const start = () => {
    if (!navigator.geolocation) {
      setStatus("GEOLOCATION UNAVAILABLE");
      return;
    }
    setActive(true);
    setStatus("ACQUIRING FIX");
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        lastRef.current = pos.coords;
        setFix({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy });
        setStatus("GPS LOCKED");
      },
      () => setStatus("GPS PERMISSION DENIED"),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
    );
    timerRef.current = setInterval(() => {
      if (lastRef.current) send(lastRef.current);
    }, PING_MS);
  };

  const stop = () => {
    setActive(false);
    setStatus("TRIP STOPPED");
    if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
    clearInterval(timerRef.current);
  };

  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex min-h-screen flex-col bg-[#0A0A0A] px-6 py-8" data-testid="driver-page">
      <div className="flex items-center gap-3">
        <BrandMark brand={brand} size={20} />
        <span className="overline mt-1">driver unit</span>
      </div>

      <div className="mt-10">
        <label className="overline mb-2 block">assigned vehicle</label>
        <select
          data-testid="driver-vehicle-select"
          value={vehicleId}
          onChange={(e) => setVehicleId(e.target.value)}
          disabled={active}
          className="w-full border border-white/15 bg-[#121212] px-4 py-3 text-sm outline-none focus:border-[#00F0FF] disabled:opacity-60"
        >
          {vehicles.map((v) => (
            <option key={v.id} value={v.id}>
              {v.plate_number}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-3">
        <div className="border border-white/10 bg-[#121212] px-4 py-4">
          <p className="overline">pings sent</p>
          <p className="font-head text-4xl font-black" data-testid="driver-ping-count">
            {pings}
          </p>
        </div>
        <div className="border border-white/10 bg-[#121212] px-4 py-4">
          <p className="overline">accuracy</p>
          <p className="font-head text-4xl font-black text-[#00F0FF]">
            {fix ? Math.round(fix.acc) : "--"}
            <span className="ml-1 text-[10px] font-normal text-[#52525B]">M</span>
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 border border-white/10 bg-[#121212] px-4 py-3 text-xs">
        <Satellite size={13} className={active ? "text-[#00FF66]" : "text-[#52525B]"} />
        <span data-testid="driver-status">{status}</span>
      </div>
      <p className="overline mt-3" data-testid="driver-coords">
        {fix ? `${fix.lat.toFixed(5)}, ${fix.lng.toFixed(5)}` : "no fix yet"} · pushes every 15s
      </p>

      <div className="mt-auto pt-10">
        {!active ? (
          <button
            data-testid="start-trip-btn"
            onClick={start}
            className="flex w-full items-center justify-center gap-3 border border-[#00FF66] bg-[#00FF66] px-6 py-8 font-head text-2xl font-black uppercase tracking-tight text-black transition-colors duration-150 hover:bg-transparent hover:text-[#00FF66]"
          >
            <Play size={26} /> START TRIP
          </button>
        ) : (
          <button
            data-testid="stop-trip-btn"
            onClick={stop}
            className="flex w-full items-center justify-center gap-3 border border-[#FF3B30] bg-[#FF3B30] px-6 py-8 font-head text-2xl font-black uppercase tracking-tight text-black transition-colors duration-150 hover:bg-transparent hover:text-[#FF3B30]"
          >
            <Square size={24} /> STOP TRIP
          </button>
        )}
      </div>
    </div>
  );
}
